// Tabblad "Bronnenlijst" van het vlak Bronnen & dekking (W6 ronde 3; V4, V5).
//
// De uitgevers die de oogst per sector gebruikt, in drie wegingen — hoog ·
// normaal · geblokkeerd — met per uitgever wat hij opbracht (documenten, door
// de lat, trendmateriaal) en wat hij aan ruis gaf (weggelaten). Een besluit
// hier (tabel bron_besluiten, migratie 0030) wint van sectors.yaml; de server
// zegt per rij waar de weging vandaan komt.
//
// Drie dingen die dit tabblad bewust NIET zelf doet:
//  · de promotieregel nabouwen. De server stuurt de regel als zin en zegt per
//    rij of hij hem haalt; wij tonen allebei.
//  · blokkeren zonder het gevolg te tonen. Eerst GET …/bronnen/gevolg — wat
//    verdwijnt uit schermen en chat, welke trends bewijs verliezen — en pas
//    dan de knop. De motivering is verplicht; de server weigert zonder (400).
//  · iets wissen. "Opruimen" blokkeert en plant in (V4); echt wissen wacht op
//    een go van de owner. De opschoonronde onderaan is daarom alleen-lezen.

import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactElement,
} from "react";

import { Card, ReasonBadge, TableHead } from "../../components/ui";
import { EmptyReason } from "../../components/Empty";
import { api } from "../../lib/api";
import type {
  BronBesluitRequest,
  BronGevolgResponse,
  BronLijstenResponse,
  BronnenResponse,
  BronRij,
} from "../../contracts";
import { color, radius, sectionLabel, size, weight } from "../../tokens";
import {
  BlokIcoon,
  HoogIcoon,
  NormaalIcoon,
  OpruimIcoon,
  PlusIcoon,
  StemIcoon,
} from "./Iconen";
import { invoer, knopDonker, knopLicht } from "./stijl";
import { Blok, aantal, cijfer, datum, getal, noot } from "./BronnenStukken";
import {
  HERKOMST_LABEL,
  WEGING_LABEL,
  leesbaar,
  tijdelijk,
  wegingLabel,
  wegingTitel,
  type Weging,
} from "./woorden";

type Besluit = BronBesluitRequest["besluit"];
/** De twee besluiten die eerst een bevestigstap openen. */
type Paneel = "geblokkeerd" | "opruimen";
/** Toevoegen kan alleen met een van deze twee wegingen (contract). */
type ToevoegWeging = Extract<Weging, "hoog" | "normaal">;
type Bronsoort = BronnenResponse["statistiek"]["bronsoort"][number];
type Kandidaat = BronLijstenResponse["kandidaten"][number];

/** De bak waarin de server documenten zonder uitgeverstype telt. Het is geen
 *  type dat je kunt kiezen (de server antwoordt "Onbekende bronsoort."); wie
 *  geen type kiest stuurt `null`. */
const NIET_GETYPEERD = "niet_getypeerd";

const ACTIES: readonly {
  besluit: Besluit;
  woord: string;
  uitleg: string;
  Icoon: () => ReactElement;
  gevaar?: boolean;
}[] = [
  {
    besluit: "voorstel",
    woord: "Stem",
    uitleg: "Stem voor deze bron. Een stem verandert de weging niet.",
    Icoon: StemIcoon,
  },
  {
    besluit: "hoog",
    woord: `Naar ${WEGING_LABEL.hoog}`,
    uitleg: `Zet de weging op ${WEGING_LABEL.hoog}.`,
    Icoon: HoogIcoon,
  },
  {
    besluit: "normaal",
    woord: `Naar ${WEGING_LABEL.normaal}`,
    uitleg: `Zet de weging op ${WEGING_LABEL.normaal}.`,
    Icoon: NormaalIcoon,
  },
  {
    besluit: "geblokkeerd",
    woord: "Blokkeren",
    uitleg: "Blokkeren: eerst zie je wat het raakt, dan vraagt het een motivering.",
    Icoon: BlokIcoon,
    gevaar: true,
  },
  {
    besluit: "opruimen",
    woord: "Opruimen",
    uitleg: "Opruimen: blokkeren én inplannen voor opruiming. Echt wissen wacht op een go van de owner.",
    Icoon: OpruimIcoon,
    gevaar: true,
  },
];

// Het domein in deze zinnen is dat van het antwoord: de server maakt er
// kleine letters van en haalt "www." weg, dus wat hij opsloeg kan anders
// ogen dan wat er getypt of getoond werd.
const ZIN_NA: Record<Besluit, (domein: string) => string> = {
  voorstel: (d) => `Opgeslagen: je stem voor ${d}.`,
  hoog: (d) => `Opgeslagen: ${d} naar ${WEGING_LABEL.hoog}.`,
  normaal: (d) => `Opgeslagen: ${d} naar ${WEGING_LABEL.normaal}.`,
  geblokkeerd: (d) => `Opgeslagen: ${d} is ${WEGING_LABEL.geblokkeerd}.`,
  opruimen: (d) => `Opgeslagen: ${d} is ${WEGING_LABEL.geblokkeerd} en gepland voor opruiming.`,
};

const isPaneel = (b: Besluit): b is Paneel => b === "geblokkeerd" || b === "opruimen";

const LIJST_KOLOMMEN =
  "minmax(210px,2.2fr) minmax(120px,1.2fr) 90px 60px 90px 110px 60px minmax(160px,1.5fr) 176px";
const KANDIDAAT_KOLOMMEN =
  "minmax(200px,2.2fr) minmax(120px,1.2fr) 90px 60px 90px 110px 70px 250px";

const icoonKnop = {
  ...knopLicht,
  padding: 0,
  width: 30,
  height: 30,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
} as const;

export function BronnenLijst({
  data,
  sector,
  bronsoorten,
  onGewijzigd,
}: {
  data: BronLijstenResponse;
  sector: string;
  /** Uit het corpus-overzicht van dezelfde sector; null zolang dat er niet is. */
  bronsoorten: readonly Bronsoort[] | null;
  /** De lijsten opnieuw ophalen na een besluit. */
  onGewijzigd: () => void;
}) {
  // Een zin die de rij moet overleven: na een besluit haalt de lijst zich
  // opnieuw op en verhuist de rij misschien naar een andere tabel.
  const [melding, setMelding] = useState<string | null>(null);
  const naBesluit = (zin: string) => {
    setMelding(zin);
    onGewijzigd();
  };

  // Een kandidaat draagt het WOORD van zijn bronsoort; het besluit vraagt de
  // sleutel. Opzoeken in wat de server al stuurde, geen eigen lijst.
  const soortSleutel = (label: string | null): string | null =>
    label === null
      ? null
      : (bronsoorten?.find((b) => b.label === label && b.sleutel !== NIET_GETYPEERD)?.sleutel ??
        null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
      <Card style={{ padding: "14px 18px" }}>
        <div style={{ ...sectionLabel }}>Promotieregel</div>
        <p style={{ fontSize: size.meta, color: color.body, lineHeight: 1.55 }}>
          {data.promotieregel.tekst}
        </p>
      </Card>

      {melding && (
        <p role="status" style={{ fontSize: size.meta, color: color.accentGreen, marginTop: -12 }}>
          {melding}
        </p>
      )}

      <LijstTabel
        titel={wegingTitel("hoog")}
        rijen={data.hoog}
        leeg={`geen enkele bron met weging ${WEGING_LABEL.hoog}`}
        sector={sector}
        naBesluit={naBesluit}
      />
      <LijstTabel
        titel={wegingTitel("normaal")}
        rijen={data.normaal}
        leeg={`geen enkele bron met weging ${WEGING_LABEL.normaal}`}
        sector={sector}
        naBesluit={naBesluit}
      />
      <LijstTabel
        titel={wegingTitel("geblokkeerd")}
        rijen={data.geblokkeerd}
        leeg={`geen enkele bron ${WEGING_LABEL.geblokkeerd} voor deze sector`}
        sector={sector}
        naBesluit={naBesluit}
      />

      <Kandidaten
        rijen={data.kandidaten}
        sector={sector}
        soortSleutel={soortSleutel}
        naBesluit={naBesluit}
      />

      <BronToevoegen sector={sector} bronsoorten={bronsoorten} naBesluit={naBesluit} />

      <Opschoon rijen={data.opschoon} />
    </div>
  );
}

function LijstTabel({
  titel,
  rijen,
  leeg,
  sector,
  naBesluit,
}: {
  titel: string;
  rijen: readonly BronRij[];
  leeg: string;
  sector: string;
  naBesluit: (zin: string) => void;
}) {
  return (
    <Blok titel={`${titel} · ${getal(rijen.length)}`}>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        {rijen.length === 0 ? (
          <div style={{ padding: 18 }}>
            <EmptyReason reason={leeg} />
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: 1080 }}>
              <TableHead
                cols={LIJST_KOLOMMEN}
                labels={[
                  "Uitgever",
                  "Bronsoort",
                  "Documenten",
                  "Door",
                  "Weggelaten",
                  "Trendmateriaal",
                  "Ratio",
                  "Herkomst",
                  "Acties",
                ]}
              />
              {rijen.map((r, i) => (
                <BronRegel
                  key={r.domein}
                  rij={r}
                  eerste={i === 0}
                  sector={sector}
                  naBesluit={naBesluit}
                />
              ))}
            </div>
          </div>
        )}
      </Card>
    </Blok>
  );
}

function BronRegel({
  rij,
  eerste,
  sector,
  naBesluit,
}: {
  rij: BronRij;
  eerste: boolean;
  sector: string;
  naBesluit: (zin: string) => void;
}) {
  const [open, setOpen] = useState<Paneel | null>(null);
  // Eén vlag voor de hele rij: ook een opslag vanuit het bevestigpaneel zet
  // hem, zodat er nooit twee besluiten over dezelfde bron tegelijk lopen.
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const paneelId = useId();

  // De knop voor de weging die hij al heeft ontbreekt; opruimen ook als hij
  // al gepland staat. Weergave op de vlaggen van de server, geen eigen regel.
  // Een hostregel uit de vaste lijst (besluitbaar = false) krijgt geen knoppen
  // maar de reden van de server: een besluit zou het hele moederdomein raken.
  const acties = rij.besluitbaar
    ? ACTIES.filter(
        (a) => a.besluit !== rij.vertrouwen && !(a.besluit === "opruimen" && rij.geplandOpruimen),
      )
    : [];

  async function doe(besluit: Besluit) {
    if (isPaneel(besluit)) {
      // Dezelfde knop nog eens = het paneel dicht.
      setFout(null);
      setOpen((o) => (o === besluit ? null : besluit));
      return;
    }
    setBezig(true);
    setFout(null);
    try {
      const resp = await api.bronBesluit({ sector, domein: rij.domein, besluit });
      naBesluit(ZIN_NA[besluit](resp.domein));
    } catch (err) {
      setFout(leesbaar(err, "Het besluit kon niet worden opgeslagen."));
    } finally {
      setBezig(false);
    }
  }

  return (
    <div
      data-bron={rij.domein}
      style={{
        display: "grid",
        gridTemplateColumns: LIJST_KOLOMMEN,
        alignItems: "center",
        padding: "10px 16px",
        borderTop: eerste ? "none" : `1px solid ${color.lineFaint}`,
        fontSize: size.meta,
        color: color.body,
      }}
    >
      <span style={{ minWidth: 0 }}>
        <span style={{ fontWeight: weight.medium, color: color.ink, wordBreak: "break-all" }}>
          {rij.domein}
        </span>
        <Badges rij={rij} />
      </span>
      <span>{rij.bronsoort ?? <EmptyReason reason="niet getypeerd" />}</span>
      <span style={cijfer}>{getal(rij.documenten)}</span>
      <span style={cijfer}>{getal(rij.door)}</span>
      <span style={cijfer}>{getal(rij.weggelaten)}</span>
      <span style={cijfer}>{getal(rij.trendmateriaal)}</span>
      <span style={cijfer} title="Deel van de documenten dat door de lat kwam">
        {rij.ratio !== null ? (
          rij.ratio.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        ) : (
          <EmptyReason reason="geen documenten" />
        )}
      </span>
      <span style={{ textAlign: "center" }}>
        <Herkomst rij={rij} />
      </span>
      <span style={{ display: "flex", gap: 4, justifyContent: "center" }}>
        {acties.map((a) => {
          const paneel = isPaneel(a.besluit);
          const uitgeklapt = open === a.besluit;
          // Staat er een paneel open, dan kan alleen zijn eigen knop nog (om
          // het te sluiten); loopt er een opslag, dan geen enkele.
          const uit = bezig || (open !== null && !uitgeklapt);
          return (
            <button
              key={a.besluit}
              type="button"
              aria-label={`${a.woord}: ${rij.domein}`}
              aria-expanded={paneel ? uitgeklapt : undefined}
              aria-controls={uitgeklapt ? paneelId : undefined}
              title={a.uitleg}
              disabled={uit}
              onClick={() => void doe(a.besluit)}
              style={{
                ...icoonKnop,
                color: a.gevaar ? color.accentRed : color.body,
                background: uitgeklapt ? color.accentAmberBg : "none",
                cursor: uit ? "default" : "pointer",
                opacity: uit && !uitgeklapt ? 0.45 : 1,
              }}
            >
              <a.Icoon />
            </button>
          );
        })}
      </span>

      {!rij.besluitbaar && rij.reden && (
        <p style={{ gridColumn: "1 / -1", color: color.faint, marginTop: 6, fontSize: 12 }}>
          {rij.reden}
        </p>
      )}
      {fout && <p style={{ gridColumn: "1 / -1", color: color.accentRed, marginTop: 6 }}>{fout}</p>}
      {open && (
        <Bevestig
          // Een nieuwe keuze = een nieuw paneel met een eigen gevolg.
          key={open}
          id={paneelId}
          sector={sector}
          domein={rij.domein}
          besluit={open}
          bezig={bezig}
          zetBezig={setBezig}
          onAnnuleer={() => setOpen(null)}
          // Na een geslaagde opslag gaat het paneel dicht. Zonder dit bleef
          // het op "bezig…" hangen als de rij in dezelfde tabel bleef staan
          // (opruimen van een al geblokkeerde bron).
          onOpgeslagen={(zin) => {
            setOpen(null);
            naBesluit(zin);
          }}
        />
      )}
    </div>
  );
}

/** Wie de weging bepaalde: de vaste lijst, of een besluit — dan met wie en
 *  wanneer, en de motivering in de tooltip. */
function Herkomst({ rij }: { rij: BronRij }) {
  if (rij.herkomst === "besluit") {
    const b = rij.besluit;
    const woord = HERKOMST_LABEL.besluit;
    const wie = b?.door ? `${woord} van ${b.door}` : woord;
    const wanneer = datum(b?.op);
    return (
      <span
        title={b?.motivering ?? "Geen motivering bij dit besluit."}
        style={{ cursor: "help", color: color.body }}
      >
        {wanneer ? `${wie} op ${wanneer}` : wie}
      </span>
    );
  }
  // Het contract noemt het bestand; de beheerder leest het woord.
  return (
    <span title="Staat in de vaste bronnenlijst van deze sector." style={{ color: color.faint }}>
      {HERKOMST_LABEL[rij.herkomst]}
    </span>
  );
}

function Badges({ rij }: { rij: BronRij }) {
  const badges: ReactElement[] = [];
  if (rij.toegevoegd) {
    badges.push(
      <ReasonBadge
        key="toegevoegd"
        text="zelf toegevoegd"
        title={`Toegevoegd via dit vlak, niet uit de ${HERKOMST_LABEL["sectors.yaml"]}.`}
      />,
    );
  }
  if (rij.geplandOpruimen) {
    badges.push(
      <ReasonBadge
        key="opruimen"
        text="gepland voor opruiming"
        tone="amber"
        title={`${wegingTitel("geblokkeerd")} en ingepland. Echt wissen wacht op een go van de owner.`}
      />,
    );
  }
  if (rij.haaltPromotieregel) {
    badges.push(
      <ReasonBadge
        key="promotie"
        text="haalt de promotieregel"
        tone="blue"
        title="Volgens de server haalt deze uitgever de promotieregel hierboven."
      />,
    );
  }
  if (rij.stemmen > 0) {
    badges.push(<ReasonBadge key="stemmen" text={aantal(rij.stemmen, "stem", "stemmen")} />);
  }
  if (!badges.length) return null;
  return (
    <span style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>{badges}</span>
  );
}

/**
 * De bevestigstap vóór blokkeren of opruimen. Eerst het gevolg van de server
 * (wat verdwijnt, welke trends bewijs verliezen), dan de motivering, dan pas
 * de knop. Zolang het gevolg er niet is, blijft de knop uit: blind blokkeren
 * is precies wat deze stap moet voorkomen.
 *
 * De lege-motivering-melding is een vormcontrole op het veld (zoals het
 * nieuwe maximum op de wachtrij); de regel zelf bewaakt de server met een 400
 * en een Nederlandse zin, en die tonen we letterlijk.
 *
 * Mislukt het gevolg met een 4xx (bv. een subdomein), dan zegt de server
 * waarom en verandert nog eens proberen daar niets aan: dan zijn zin, zonder
 * "Opnieuw". Alleen bij geen verbinding of een storing mag het nog eens.
 */
type GevolgStaat =
  | { soort: "laden" }
  | { soort: "klaar"; data: BronGevolgResponse }
  | { soort: "fout"; zin: string; opnieuw: boolean };

function Bevestig({
  id,
  sector,
  domein,
  besluit,
  bezig,
  zetBezig,
  onAnnuleer,
  onOpgeslagen,
}: {
  id: string;
  sector: string;
  domein: string;
  besluit: Paneel;
  /** Van de rij: loopt er een opslag voor deze bron? */
  bezig: boolean;
  zetBezig: (bezig: boolean) => void;
  onAnnuleer: () => void;
  onOpgeslagen: (zin: string) => void;
}) {
  const [gevolg, setGevolg] = useState<GevolgStaat>({ soort: "laden" });
  const [poging, setPoging] = useState(0);
  const [motivering, setMotivering] = useState("");
  const [hint, setHint] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const hintId = useId();
  const veld = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let levend = true;
    api.bronGevolg(sector, domein).then(
      (data) => {
        if (levend) setGevolg({ soort: "klaar", data });
      },
      (err: unknown) => {
        if (!levend) return;
        setGevolg({
          soort: "fout",
          zin: leesbaar(err, "Probeer het zo nog eens."),
          opnieuw: tijdelijk(err),
        });
      },
    );
    return () => {
      levend = false;
    };
  }, [sector, domein, poging]);

  // Wie het paneel opent, gaat verder in het veld dat hij toch moet invullen.
  useEffect(() => {
    veld.current?.focus();
  }, []);

  const woord = besluit === "opruimen" ? "opruimen" : "blokkeren";
  const uit = gevolg.soort !== "klaar" || bezig;

  async function bevestig() {
    if (!motivering.trim()) {
      setHint(true);
      return;
    }
    setHint(false);
    setFout(null);
    zetBezig(true);
    try {
      const resp = await api.bronBesluit({ sector, domein, besluit, motivering: motivering.trim() });
      zetBezig(false);
      onOpgeslagen(ZIN_NA[besluit](resp.domein));
    } catch (err) {
      setFout(leesbaar(err, "Het besluit kon niet worden opgeslagen."));
      zetBezig(false);
    }
  }

  function opEnter(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (!uit) void bevestig();
  }

  return (
    <div
      id={id}
      role="group"
      aria-label={`Bevestig ${woord} van ${domein}`}
      style={{
        gridColumn: "1 / -1",
        marginTop: 10,
        padding: "12px 14px",
        background: color.accentAmberBg,
        border: "1px solid #fde68a",
        borderRadius: radius.block,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        lineHeight: 1.55,
      }}
    >
      <strong style={{ color: color.ink, fontWeight: weight.medium }}>
        {`${domein} ${woord}?`}
      </strong>

      {gevolg.soort === "fout" ? (
        <p
          data-gevolg-fout
          style={{
            color: color.accentRed,
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 10px",
            alignItems: "center",
          }}
        >
          <span>Wat dit raakt kon niet worden opgehaald.</span>
          <span>{gevolg.zin}</span>
          {gevolg.opnieuw && (
            <button
              type="button"
              onClick={() => {
                setGevolg({ soort: "laden" });
                setPoging((p) => p + 1);
              }}
              style={{ ...knopLicht, padding: "4px 10px" }}
            >
              Opnieuw
            </button>
          )}
        </p>
      ) : gevolg.soort === "klaar" ? (
        <p data-gevolg style={{ color: color.body }}>
          {gevolg.data.tekst}
        </p>
      ) : (
        <EmptyReason reason="wat dit raakt wordt opgehaald" />
      )}

      {besluit === "opruimen" && (
        <p style={{ color: color.soft, fontSize: 12 }}>
          Opruimen = blokkeren én inplannen voor opruiming. Echt wissen gebeurt pas na een go van
          de owner.
        </p>
      )}

      <input
        ref={veld}
        type="text"
        value={motivering}
        onKeyDown={opEnter}
        onChange={(e) => {
          setMotivering(e.target.value);
          if (e.target.value.trim()) setHint(false);
        }}
        placeholder="Waarom? Verplicht — het komt in het logboek."
        aria-label={`Motivering bij het ${woord}`}
        aria-invalid={hint ? true : undefined}
        aria-describedby={hint ? hintId : undefined}
        style={{ ...invoer, background: color.surface }}
      />
      {hint && (
        <p id={hintId} style={{ color: color.accentAmber }}>
          {`Vul eerst een motivering in: zeg waarom je deze bron ${
            besluit === "opruimen" ? "opruimt" : "blokkeert"
          }.`}
        </p>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" onClick={() => void bevestig()} disabled={uit} style={knopDonker(uit)}>
          {bezig ? "bezig…" : `Ja, ${woord}`}
        </button>
        {/* Annuleren tijdens een opslag houdt de aanroep niet tegen; dan
            liever wachten op de uitkomst. */}
        <button
          type="button"
          onClick={onAnnuleer}
          disabled={bezig}
          style={{
            ...knopLicht,
            background: color.surface,
            cursor: bezig ? "default" : "pointer",
          }}
        >
          Annuleren
        </button>
      </div>
      {fout && <p style={{ color: color.accentRed }}>{fout}</p>}
    </div>
  );
}

function Kandidaten({
  rijen,
  sector,
  soortSleutel,
  naBesluit,
}: {
  rijen: readonly Kandidaat[];
  sector: string;
  soortSleutel: (label: string | null) => string | null;
  naBesluit: (zin: string) => void;
}) {
  return (
    <Blok titel={`Kandidaten van buiten de lijst · ${getal(rijen.length)}`}>
      <Card style={{ padding: 0, overflow: "hidden" }}>
        {rijen.length === 0 ? (
          <div style={{ padding: 18 }}>
            <EmptyReason reason="geen uitgever van buiten de lijst haalt nu de promotieregel" />
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: 1000 }}>
              <TableHead
                cols={KANDIDAAT_KOLOMMEN}
                labels={[
                  "Uitgever",
                  "Bronsoort",
                  "Documenten",
                  "Door",
                  "Weggelaten",
                  "Trendmateriaal",
                  "Stemmen",
                  "Acties",
                ]}
              />
              {rijen.map((k, i) => (
                <KandidaatRegel
                  key={k.domein}
                  kandidaat={k}
                  eerste={i === 0}
                  sector={sector}
                  soortSleutel={soortSleutel}
                  naBesluit={naBesluit}
                />
              ))}
            </div>
          </div>
        )}
      </Card>
      <p style={noot}>Uitgevers die nog niet op de lijst staan maar de promotieregel wel halen.</p>
    </Blok>
  );
}

function KandidaatRegel({
  kandidaat: k,
  eerste,
  sector,
  soortSleutel,
  naBesluit,
}: {
  kandidaat: Kandidaat;
  eerste: boolean;
  sector: string;
  soortSleutel: (label: string | null) => string | null;
  naBesluit: (zin: string) => void;
}) {
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  async function doe(besluit: "voorstel" | ToevoegWeging) {
    // Toevoegen = een nieuwe bron met weging hoog of normaal; een stem voegt
    // niets toe (de server weigert "toegevoegd" bij een stem).
    const body: BronBesluitRequest =
      besluit === "voorstel"
        ? { sector, domein: k.domein, besluit }
        : { sector, domein: k.domein, besluit, toegevoegd: true, bronsoort: soortSleutel(k.bronsoort) };
    setBezig(true);
    setFout(null);
    try {
      const resp = await api.bronBesluit(body);
      naBesluit(
        besluit === "voorstel"
          ? ZIN_NA.voorstel(resp.domein)
          : `Opgeslagen: ${resp.domein} toegevoegd als ${WEGING_LABEL[besluit]}.`,
      );
    } catch (err) {
      setFout(leesbaar(err, "Het besluit kon niet worden opgeslagen."));
    } finally {
      setBezig(false);
    }
  }

  const toevoegKnop = (weging: ToevoegWeging) => (
    <button
      type="button"
      aria-label={`Toevoegen als ${WEGING_LABEL[weging]}: ${k.domein}`}
      title={`Op de lijst zetten met weging ${WEGING_LABEL[weging]}`}
      disabled={bezig}
      onClick={() => void doe(weging)}
      style={{
        ...knopLicht,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "5px 10px",
        cursor: bezig ? "default" : "pointer",
      }}
    >
      <PlusIcoon /> {WEGING_LABEL[weging]}
    </button>
  );

  return (
    <div
      data-kandidaat={k.domein}
      style={{
        display: "grid",
        gridTemplateColumns: KANDIDAAT_KOLOMMEN,
        alignItems: "center",
        padding: "10px 16px",
        borderTop: eerste ? "none" : `1px solid ${color.lineFaint}`,
        fontSize: size.meta,
        color: color.body,
      }}
    >
      <span style={{ fontWeight: weight.medium, color: color.ink, wordBreak: "break-all" }}>
        {k.domein}
      </span>
      <span>{k.bronsoort ?? <EmptyReason reason="niet getypeerd" />}</span>
      <span style={cijfer}>{getal(k.documenten)}</span>
      <span style={cijfer}>{getal(k.door)}</span>
      <span style={cijfer}>{getal(k.weggelaten)}</span>
      <span style={cijfer}>{getal(k.trendmateriaal)}</span>
      <span style={{ ...cijfer, textAlign: "center" }}>{getal(k.stemmen)}</span>
      <span style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          aria-label={`Stem: ${k.domein}`}
          title="Stem voor deze bron. Een stem voegt hem nog niet toe."
          disabled={bezig}
          onClick={() => void doe("voorstel")}
          style={{ ...icoonKnop, cursor: bezig ? "default" : "pointer" }}
        >
          <StemIcoon />
        </button>
        {toevoegKnop("hoog")}
        {toevoegKnop("normaal")}
      </span>
      {fout && <p style={{ gridColumn: "1 / -1", color: color.accentRed, marginTop: 6 }}>{fout}</p>}
    </div>
  );
}

/** Letterlijk het patroon uit het contract (beheer.schema.json#bronBesluitRequest). */
const DOMEIN_PATROON = /^[.]?([wW]{3}[.])?[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]$/;

/**
 * Het domein uit het veld, of een reden waarom het er zo niet in kan.
 * Waarom het scherm het zelf nakijkt: bij een afwijkend domein antwoordt
 * FastAPI met een Engelstalige validatielijst, die de gebruiker niets zegt.
 * Een vormcontrole op het veld, niet strenger dan het contract en geen
 * nagebouwde regel over wélke bron mag: kleine letters, "www." en een
 * subdomein regelt de server (een subdomein krijgt een 400 die het goede
 * domein noemt, en die zin tonen we letterlijk).
 */
function leesDomein(tekst: string): { domein: string; fout: null } | { domein: null; fout: string } {
  const d = tekst.trim();
  if (!d) return { domein: null, fout: "Vul een domein in, zoals voorbeeld.nl." };
  if (!DOMEIN_PATROON.test(d)) {
    return {
      domein: null,
      fout: "Alleen de domeinnaam, zoals voorbeeld.nl — zonder https://, pad of spaties.",
    };
  }
  return { domein: d, fout: null };
}

function BronToevoegen({
  sector,
  bronsoorten,
  naBesluit,
}: {
  sector: string;
  bronsoorten: readonly Bronsoort[] | null;
  naBesluit: (zin: string) => void;
}) {
  const [domein, setDomein] = useState("");
  const [weging, setWeging] = useState<ToevoegWeging>("normaal");
  const [bronsoort, setBronsoort] = useState("");
  const [motivering, setMotivering] = useState("");
  const [vormFout, setVormFout] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const id = useId();

  // Alleen de sleutels die het corpus van deze sector kent (research §3.4);
  // "niet getypeerd" is geen keuze maar het lege veld.
  const keuzes = (bronsoorten ?? []).filter((b) => b.sleutel !== NIET_GETYPEERD);

  async function voegToe(e: FormEvent) {
    e.preventDefault();
    const d = leesDomein(domein);
    if (d.domein === null) {
      setVormFout(d.fout);
      return;
    }
    setVormFout(null);
    setFout(null);
    setBezig(true);
    try {
      const resp = await api.bronBesluit({
        sector,
        domein: d.domein,
        besluit: weging,
        toegevoegd: true,
        bronsoort: bronsoort || null,
        motivering: motivering.trim() || null,
      });
      setDomein("");
      setBronsoort("");
      setMotivering("");
      setWeging("normaal");
      // Het domein dat de server opsloeg, niet wat er getypt werd.
      naBesluit(`Opgeslagen: ${resp.domein} toegevoegd als ${WEGING_LABEL[weging]}.`);
    } catch (err) {
      setFout(leesbaar(err, "De bron kon niet worden toegevoegd."));
    } finally {
      setBezig(false);
    }
  }

  const label = { fontSize: 12, color: color.soft, marginBottom: 4, display: "block" } as const;

  return (
    <Blok titel="Bron toevoegen">
      <Card style={{ padding: "14px 18px" }}>
        <form onSubmit={(e) => void voegToe(e)} noValidate>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))",
              gap: 12,
            }}
          >
            <div>
              <label htmlFor={`${id}-domein`} style={label}>
                Domein
              </label>
              <input
                id={`${id}-domein`}
                type="text"
                value={domein}
                onChange={(e) => setDomein(e.target.value)}
                placeholder="voorbeeld.nl"
                aria-invalid={vormFout ? true : undefined}
                aria-describedby={vormFout ? `${id}-vorm` : undefined}
                style={invoer}
              />
            </div>
            <div>
              <label htmlFor={`${id}-weging`} style={label}>
                Weging
              </label>
              <select
                id={`${id}-weging`}
                value={weging}
                onChange={(e) => setWeging(e.target.value === "hoog" ? "hoog" : "normaal")}
                style={invoer}
              >
                <option value="normaal">{wegingTitel("normaal")}</option>
                <option value="hoog">{wegingTitel("hoog")}</option>
              </select>
            </div>
            <div>
              <label htmlFor={`${id}-soort`} style={label}>
                Bronsoort
              </label>
              <select
                id={`${id}-soort`}
                value={bronsoort}
                onChange={(e) => setBronsoort(e.target.value)}
                style={invoer}
              >
                <option value="">Geen (niet getypeerd)</option>
                {keuzes.map((b) => (
                  <option key={b.sleutel} value={b.sleutel}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={`${id}-motivering`} style={label}>
                Motivering (optioneel)
              </label>
              <input
                id={`${id}-motivering`}
                type="text"
                value={motivering}
                onChange={(e) => setMotivering(e.target.value)}
                placeholder="Waarom deze bron?"
                style={invoer}
              />
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
            <button
              type="submit"
              disabled={bezig}
              style={{ ...knopDonker(bezig), display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <PlusIcoon /> {bezig ? "bezig…" : "Toevoegen"}
            </button>
            {bronsoorten === null && (
              <EmptyReason reason="de bronsoorten komen uit het corpus-overzicht; dat is nog niet opgehaald" />
            )}
          </div>
          {vormFout && (
            <p id={`${id}-vorm`} style={{ color: color.accentAmber, marginTop: 8, fontSize: size.meta }}>
              {vormFout}
            </p>
          )}
          {fout && <p style={{ color: color.accentRed, marginTop: 8, fontSize: size.meta }}>{fout}</p>}
        </form>
      </Card>
    </Blok>
  );
}

function Opschoon({ rijen }: { rijen: BronLijstenResponse["opschoon"] }) {
  return (
    <Blok titel={`Opschoonronde · ${getal(rijen.length)}`}>
      <Card style={{ padding: rijen.length ? 0 : 18, maxHeight: 360, overflowY: "auto" }}>
        {rijen.length === 0 ? (
          <EmptyReason reason="geen bron op de lijst die voor opruiming in aanmerking komt" />
        ) : (
          <ul style={{ listStyle: "none" }}>
            {rijen.map((o, i) => (
              <li
                key={o.domein}
                data-opschoon={o.domein}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  gap: 10,
                  padding: "8px 16px",
                  borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                  fontSize: size.meta,
                }}
              >
                <span style={{ color: color.ink, fontWeight: weight.medium }}>{o.domein}</span>
                <ReasonBadge text={wegingLabel(o.vertrouwen)} title="Huidige weging" />
                <span style={{ color: color.faint }}>{o.reden}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <p style={noot}>
        Alleen-lezen. Echt wissen wacht op een go van de owner; tot die tijd kun je een bron
        hierboven blokkeren of opruimen.
      </p>
    </Blok>
  );
}
